import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppUpdateHistoryModal from '@/components/feature/AppInfoModal';
import { useAuth } from '@/hooks/useAuth';

function MenuRow({
  icon,
  title,
  description,
  to,
  onClick,
  accent = false,
}: {
  icon: string;
  title: string;
  description?: string;
  to?: string;
  onClick?: () => void;
  accent?: boolean;
}) {
  const content = (
    <>
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${accent ? 'bg-accent-100 text-accent-600' : 'bg-secondary-100 text-foreground-600'}`}>
          <i className={`${icon} text-lg`} />
        </div>
        <div className="text-left">
          <span className="block text-sm font-medium text-foreground-900">{title}</span>
          {description && <span className="mt-0.5 block text-[11px] leading-relaxed text-foreground-400">{description}</span>}
        </div>
      </div>
      <i className="ri-arrow-right-s-line flex-shrink-0 text-foreground-400" />
    </>
  );

  const className = 'flex w-full items-center justify-between border-b border-background-200 px-4 py-4 last:border-b-0 transition-colors hover:bg-secondary-50';
  if (to) return <Link to={to} className={className}>{content}</Link>;
  return <button type="button" onClick={onClick} className={className}>{content}</button>;
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUpdateHistory, setShowUpdateHistory] = useState(false);
  return (
    <div className="min-h-screen bg-background-50 pb-28">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 py-4 backdrop-blur">
        <button type="button" onClick={() => navigate(-1)} className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-100" aria-label="뒤로 가기">
          <i className="ri-arrow-left-line text-xl" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground-950">설정</h1>
          <p className="text-xs text-foreground-400">계정과 앱 사용 방법을 관리합니다</p>
        </div>
      </header>

      <main className="space-y-6 p-4">
        <section className="rounded-3xl bg-foreground-900 p-5 text-white">
          <p className="text-xs text-white/55">현재 계정</p>
          <div className="mt-2 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold">{user?.name || '가족 사용자'}</h2>
              <p className="mt-1 text-sm text-white/65">{user?.familyName || '우리 가족'} · {user?.role === 'admin' ? '관리자' : '구성원'}</p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl font-bold">
              {(user?.name || '가').slice(0, 1)}
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-2 px-1 text-xs font-semibold text-foreground-400">내 계정</h2>
          <div className="overflow-hidden rounded-2xl border border-background-200 bg-white">
            <MenuRow icon="ri-key-2-line" title="로그인 정보 변경" description="아이디와 비밀번호를 변경합니다" to="/profile/account" />
          </div>
        </section>

        <section>
          <h2 className="mb-2 px-1 text-xs font-semibold text-foreground-400">알림</h2>
          <div className="overflow-hidden rounded-2xl border border-background-200 bg-white">
            <MenuRow icon="ri-notification-3-line" title="알림 설정" description="추억 알림 시간과 휴대폰 알림을 설정합니다" to="/settings/notifications" accent />
            <MenuRow icon="ri-inbox-archive-line" title="받은 알림" description="최근 7일 동안 받은 추억 알림을 확인합니다" to="/notifications" />
          </div>
        </section>

        <section>
          <h2 className="mb-2 px-1 text-xs font-semibold text-foreground-400">앱 사용</h2>
          <div className="overflow-hidden rounded-2xl border border-background-200 bg-white">
            <MenuRow icon="ri-smartphone-line" title="앱 설치 · 가족에게 공유" description="휴대폰에 설치하고 설치 링크를 가족에게 보내세요" to="/install" accent />
            <MenuRow icon="ri-history-line" title="앱 업데이트 내역" description="이번 버전에서 달라진 내용을 확인합니다" onClick={() => setShowUpdateHistory(true)} />
          </div>
        </section>

        {user?.role === 'admin' && (
          <section>
            <h2 className="mb-2 px-1 text-xs font-semibold text-foreground-400">관리자 설정</h2>
            <div className="overflow-hidden rounded-2xl border border-background-200 bg-white">
              <MenuRow icon="ri-layout-4-line" title="홈 화면 꾸미기" description="메인 화면 문구와 표시 내용을 설정합니다" to="/admin/home-settings" accent />
              <MenuRow icon="ri-shield-star-line" title="관리자 페이지" description="사용자 승인과 가족별 관리 기능을 엽니다" to="/admin" accent />
            </div>
          </section>
        )}

        <section>
          <button type="button" onClick={logout} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-100 bg-rose-50 py-4 text-sm font-semibold text-rose-600">
            <i className="ri-logout-box-r-line" />
            로그아웃
          </button>
        </section>
      </main>

      <AppUpdateHistoryModal isOpen={showUpdateHistory} onClose={() => setShowUpdateHistory(false)} />
    </div>
  );
}
